import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Calculator, CircleDollarSign, PhoneCall, RefreshCw, Send, TrendingUp, Users } from "lucide-react";
import { getAnalytics, getGoogleAdsReport, AuthError } from "./api";
import { BarChart } from "./charts";
import { formatCurrency, formatNumber, productLabel, relativeTime } from "./format";
import type { AnalyticsLiveEvent, AnalyticsReport, GoogleAdsReport } from "./types";

const RANGE_OPTIONS = [
  { value: 1, label: "1 dzień" },
  { value: 7, label: "7 dni" },
  { value: 30, label: "30 dni" },
  { value: 90, label: "90 dni" }
];

function percent(value: number) {
  return `${new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 1 }).format(value)}%`;
}

function money(value: number, currency: string) {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: currency || "PLN",
    maximumFractionDigits: 2
  }).format(value);
}

function safePageLabel(page: string) {
  try {
    const url = new URL(page);
    return url.pathname || "/";
  } catch {
    return page || "/";
  }
}

function eventLabel(name: string) {
  const labels: Record<string, string> = {
    page_view: "Wejście na stronę",
    oblicz_skladke: "Obliczono składkę",
    telefon_click: "Klik telefonu",
    formularz_start: "Start formularza",
    lead_success: "Wysłany lead",
    lead_error: "Błąd wysyłki"
  };

  return labels[name] ?? name;
}

function eventTone(name: string) {
  if (name === "lead_success") return "green";
  if (name === "telefon_click") return "amber";
  if (name === "formularz_start") return "violet";
  if (name === "oblicz_skladke") return "blue";
  if (name === "lead_error") return "red";
  return "navy";
}

function locationLabel(event: AnalyticsLiveEvent) {
  const place = event.city || event.region;
  if (place && event.country) return `${place}, ${event.country}`;
  return place || event.country || "";
}

function deviceLabel(event: AnalyticsLiveEvent) {
  const labels: Record<string, string> = {
    mobile: "telefon",
    tablet: "tablet",
    desktop: "desktop"
  };
  return labels[event.device] ?? event.device;
}

function isPaidEvent(event: AnalyticsLiveEvent) {
  const medium = event.medium.toLowerCase();
  const source = event.source.toLowerCase();
  return Boolean(
    event.gclid ||
      event.gbraid ||
      event.wbraid ||
      medium === "cpc" ||
      medium === "ppc" ||
      medium === "paid" ||
      medium === "paid_search" ||
      (source === "google" && medium.includes("cpc"))
  );
}

export function AnalyticsView({
  days,
  onDaysChange,
  onAuthError
}: {
  days: number;
  onDaysChange: (days: number) => void;
  onAuthError: () => void;
}) {
  const [report, setReport] = useState<AnalyticsReport | null>(null);
  const [adsReport, setAdsReport] = useState<GoogleAdsReport | null>(null);
  const [adsState, setAdsState] = useState<"ready" | "error">("ready");
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");

  const load = async (range = days, silent = false) => {
    if (!silent) setLoadState("loading");
    try {
      const [analyticsData, googleAdsData] = await Promise.all([
        getAnalytics(range),
        getGoogleAdsReport(range).catch((error) => {
          if (error instanceof AuthError) throw error;
          setAdsState("error");
          return null;
        })
      ]);
      setReport(analyticsData);
      setAdsReport(googleAdsData);
      if (googleAdsData) setAdsState("ready");
      setLoadState("ready");
    } catch (error) {
      if (error instanceof AuthError) {
        onAuthError();
        return;
      }
      setLoadState("error");
    }
  };

  useEffect(() => {
    void load(days);
    const timer = window.setInterval(() => {
      void load(days, true);
    }, 8000);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const chartData = useMemo(
    () => (report?.daily || []).map((item) => ({ label: item.label, value: item.sessions })),
    [report]
  );

  if (loadState === "loading" && !report) {
    return <div className="crm-state">Ładowanie analityki…</div>;
  }

  if (loadState === "error" && !report) {
    return (
      <div className="crm-state crm-state--error">
        <p>Nie udało się pobrać analityki.</p>
        <button type="button" className="crm-btn crm-btn--primary" onClick={() => void load()}>
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  if (!report) return null;

  const summary = report.summary;
  const maxFunnel = Math.max(1, ...report.funnel.map((step) => step.value));

  return (
    <div className="crm-view">
      <div className="crm-analytics-head">
        <div>
          <p className="crm-hint">Dane są zapisywane tylko po zgodzie analitycznej. Bez telefonu, treści formularza i e-maila.</p>
        </div>
        <div className="crm-analytics-controls">
          <select
            className="crm-select"
            value={days}
            onChange={(event) => onDaysChange(Number(event.target.value))}
            aria-label="Zakres analityki"
          >
            {RANGE_OPTIONS.map((option) => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="crm-icon-btn"
            onClick={() => void load()}
            title="Odśwież analitykę"
            disabled={loadState === "loading"}
          >
            <RefreshCw size={18} aria-hidden="true" className={loadState === "loading" ? "crm-spin" : undefined} />
          </button>
        </div>
      </div>

      <LiveFeed events={report.liveEvents ?? []} />

      <div className="crm-kpis">
        <AnalyticsKpi icon={<Users size={18} />} tone="blue" label="Sesje" value={formatNumber(summary.sessions)} sub={`${formatNumber(summary.pageViews)} wejść`} />
        <AnalyticsKpi icon={<CircleDollarSign size={18} />} tone="green" label="Płatny ruch" value={formatNumber(summary.paidSessions)} sub={`${formatNumber(summary.paidLeads)} leady z reklam`} />
        <AnalyticsKpi icon={<Calculator size={18} />} tone="violet" label="Obliczenia" value={formatNumber(summary.calculatorResults)} sub="użycia kalkulatorów" />
        <AnalyticsKpi icon={<PhoneCall size={18} />} tone="blue" label="Klik telefon" value={formatNumber(summary.phoneClicks)} sub="kliknięcia tel." />
        <AnalyticsKpi icon={<TrendingUp size={18} />} tone="navy" label="Start formularza" value={formatNumber(summary.formStarts)} sub={`${formatNumber(summary.events)} zdarzeń`} />
        <AnalyticsKpi icon={<Send size={18} />} tone="green" label="Leady" value={formatNumber(summary.leads)} sub={`${percent(summary.leadConversion)} z sesji`} accent />
      </div>

      <GoogleAdsPanel adsReport={adsReport} state={adsState} summary={summary} paidCampaigns={report.paidCampaigns} />

      <div className="crm-grid-2">
        <section className="crm-card">
          <header className="crm-card-head">
            <h2>Sesje dziennie</h2>
          </header>
          <BarChart data={chartData} height={170} accent="#2f80ed" />
        </section>

        <section className="crm-card">
          <header className="crm-card-head">
            <h2>Lejek użytkownika</h2>
          </header>
          <ul className="crm-analytics-funnel">
            {report.funnel.map((step) => (
              <li key={step.key}>
                <span>{step.label}</span>
                <span className="crm-analytics-funnel-bar">
                  <span style={{ width: `${(step.value / maxFunnel) * 100}%` }} />
                </span>
                <strong>{formatNumber(step.value)}</strong>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="crm-grid-2">
        <section className="crm-card">
          <header className="crm-card-head">
            <h2>Kalkulatory</h2>
          </header>
          {report.calculators.length ? (
            <div className="crm-analytics-table-wrap">
              <table className="crm-analytics-table">
                <thead>
                  <tr>
                    <th>Produkt</th>
                    <th>Obliczenia</th>
                    <th>Leady</th>
                    <th>Konw.</th>
                  </tr>
                </thead>
                <tbody>
                  {report.calculators.map((item) => (
                    <tr key={item.name}>
                      <td>{productLabel(item.name, true)}</td>
                      <td>{formatNumber(item.calculations)}</td>
                      <td>{formatNumber(item.leads)}</td>
                      <td>{percent(item.conversion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="crm-empty">Brak danych o kalkulatorach w tym okresie.</p>
          )}
        </section>

        <section className="crm-card">
          <header className="crm-card-head">
            <h2>Najczęściej oglądane strony</h2>
          </header>
          {report.topPages.length ? (
            <ol className="crm-analytics-list">
              {report.topPages.map((item) => (
                <li key={item.page}>
                  <span>{safePageLabel(item.page)}</span>
                  <strong>{formatNumber(item.views)}</strong>
                </li>
              ))}
            </ol>
          ) : (
            <p className="crm-empty">Brak odsłon po zgodzie analitycznej.</p>
          )}
        </section>
      </div>

      <div className="crm-grid-2">
        <AnalyticsListCard title="Źródła sesji" items={report.sources.map((item) => ({ label: item.source, value: item.sessions }))} />
        <AnalyticsListCard title="Urządzenia" items={report.devices.map((item) => ({ label: item.device, value: item.sessions }))} />
      </div>
    </div>
  );
}

function googleAdsCostNote(report: GoogleAdsReport | null, state: "ready" | "error") {
  if (state === "error") return "Koszt i CPC: nie udało się pobrać z Google Ads przy tym odświeżeniu.";
  if (!report || !report.configured) {
    return "Koszt, CPC i wyświetlenia pojawią się po dodaniu sekretów Google Ads API.";
  }
  if (report.error) {
    if (/DEVELOPER_TOKEN_NOT_APPROVED|test account|only approved/i.test(report.error)) {
      return "Koszt, CPC i wyświetlenia czekają na zatwierdzenie Basic access dla tokenu API — na razie token działa tylko z kontami testowymi. Wniosek składasz w Google Ads API Center.";
    }
    return `Koszt i CPC z Google Ads: ${report.error}`;
  }
  return "";
}

function GoogleAdsPanel({
  adsReport,
  state,
  summary,
  paidCampaigns
}: {
  adsReport: GoogleAdsReport | null;
  state: "ready" | "error";
  summary: AnalyticsReport["summary"];
  paidCampaigns: AnalyticsReport["paidCampaigns"];
}) {
  const hasApiData = Boolean(adsReport?.configured && !adsReport.error && state === "ready");
  const currency = adsReport?.currency || "PLN";
  const paidConversion =
    summary.paidSessions > 0 ? Math.round((summary.paidLeads / summary.paidSessions) * 1000) / 10 : 0;
  const costNote = googleAdsCostNote(adsReport, state);

  return (
    <section className="crm-card crm-google-ads">
      <header className="crm-card-head">
        <div>
          <h2>Reklamy Google</h2>
          <p>Kliknięcia, kampanie i leady liczymy na stronie. Koszt i CPC dochodzą z konta Google Ads, gdy API jest połączone.</p>
        </div>
        <span className={`crm-pill ${hasApiData ? "blue" : ""}`}>{hasApiData ? "API połączone" : "dane własne"}</span>
      </header>

      <div className="crm-ad-metrics">
        <span>
          <small>Kliknięcia z reklam</small>
          <strong>{formatNumber(summary.paidSessions)}</strong>
        </span>
        <span>
          <small>Leady z reklam</small>
          <strong>{formatNumber(summary.paidLeads)}</strong>
        </span>
        <span>
          <small>Konwersja płatnego ruchu</small>
          <strong>{percent(paidConversion)}</strong>
        </span>
      </div>

      {paidCampaigns.length ? (
        <div className="crm-analytics-table-wrap">
          <table className="crm-analytics-table">
            <thead>
              <tr>
                <th>Kampania</th>
                <th>Kliknięcia</th>
                <th>Leady</th>
                <th>Konw.</th>
              </tr>
            </thead>
            <tbody>
              {paidCampaigns.map((campaign) => (
                <tr key={campaign.campaign}>
                  <td>{campaign.campaign}</td>
                  <td>{formatNumber(campaign.clicks)}</td>
                  <td>{formatNumber(campaign.leads)}</td>
                  <td>{percent(campaign.conversion)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="crm-empty">
          Brak płatnych wejść w tym okresie. Tabela wypełni się przy kliknięciach z Google Ads (gclid albo utm_source=google / utm_medium=cpc).
        </p>
      )}

      <p className="crm-hint">Koszty — z konta Google Ads (API)</p>
      {hasApiData && adsReport ? (
        <div className="crm-ad-metrics">
          <span>
            <small>Koszt</small>
            <strong>{money(adsReport.summary.cost, currency)}</strong>
          </span>
          <span>
            <small>Wyświetlenia</small>
            <strong>{formatNumber(adsReport.summary.impressions)}</strong>
          </span>
          <span>
            <small>Śr. CPC</small>
            <strong>{money(adsReport.summary.averageCpc, currency)}</strong>
          </span>
          <span>
            <small>CTR</small>
            <strong>{percent(adsReport.summary.ctr)}</strong>
          </span>
          <span>
            <small>Koszt / lead z reklam</small>
            <strong>{summary.paidLeads > 0 ? money(adsReport.summary.cost / summary.paidLeads, currency) : "—"}</strong>
          </span>
        </div>
      ) : (
        <p className="crm-empty">{costNote}</p>
      )}
    </section>
  );
}

function LiveFeed({ events }: { events: AnalyticsLiveEvent[] }) {
  return (
    <section className="crm-card crm-live-feed">
      <header className="crm-card-head crm-live-head">
        <div>
          <h2>Live feed</h2>
          <p>Ostatnie zdarzenia na stronie. Najnowsze pojawiają się u góry, odświeżanie co 8 sekund.</p>
        </div>
        <span className="crm-live-pill">
          <span aria-hidden="true" />
          Na żywo
        </span>
      </header>

      {events.length ? (
        <ol className="crm-live-list">
          {events.map((event) => {
            const product = event.product || event.calculator;
            const meta = [
              product ? productLabel(product, true) : "",
              event.amount != null ? formatCurrency(event.amount) : "",
              isPaidEvent(event) ? "Google Ads / płatny ruch" : "",
              event.campaign ? `kampania: ${event.campaign}` : "",
              event.source || "",
              deviceLabel(event),
              locationLabel(event)
            ].filter(Boolean);

            return (
              <li key={event.id}>
                <span className={`crm-live-dot ${eventTone(event.name)}`} aria-hidden="true" />
                <div className="crm-live-main">
                  <strong>{eventLabel(event.name)}</strong>
                  <span>{safePageLabel(event.page)}</span>
                </div>
                <div className="crm-live-meta">
                  {meta.length > 0 && <span>{meta.join(" · ")}</span>}
                  <time dateTime={event.createdAt}>{relativeTime(event.createdAt)}</time>
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="crm-empty">Brak zdarzeń w wybranym okresie albo użytkownicy nie wyrazili zgody analitycznej.</p>
      )}
    </section>
  );
}

function AnalyticsKpi({
  icon,
  tone,
  label,
  value,
  sub,
  accent = false
}: {
  icon: ReactNode;
  tone: string;
  label: string;
  value: string;
  sub: string;
  accent?: boolean;
}) {
  return (
    <section className={`crm-kpi${accent ? " crm-kpi--accent" : ""}`}>
      <span className={`crm-kpi-icon ${tone}`}>{icon}</span>
      <span className="crm-kpi-label">{label}</span>
      <strong className="crm-kpi-value">{value}</strong>
      <span className="crm-kpi-sub">{sub}</span>
    </section>
  );
}

function AnalyticsListCard({ title, items }: { title: string; items: Array<{ label: string; value: number }> }) {
  return (
    <section className="crm-card">
      <header className="crm-card-head">
        <h2>{title}</h2>
      </header>
      {items.length ? (
        <ol className="crm-analytics-list">
          {items.map((item) => (
            <li key={item.label}>
              <span>{item.label || "nieznane"}</span>
              <strong>{formatNumber(item.value)}</strong>
            </li>
          ))}
        </ol>
      ) : (
        <p className="crm-empty">Brak danych w tym okresie.</p>
      )}
    </section>
  );
}
